import { useEffect } from "react";
import { getCompagnies } from "store/beneficiaryStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TPortalType } from "store/types";

interface UseCompanySettingsProps {
    portalType?: Exclude<TPortalType, "urssaf">;
}

/**
 * Hook for getting company infos (name, siret, etc) and settings (uses_portal, uses_stock, etc)
 * @example ```ts
 * const { beneficiarCompagnies, getBPCompanySettings } = useBPCompanySettings()
 * const usesMultipleBrand = getBPCompanySettings("uses_multiple_brands")
 * ```
 * @returns Company infos and getCompanySettings
 */
export const useBPCompanySettings = (props?: UseCompanySettingsProps) => {
    const portalType = props?.portalType;
    const { beneficiarCompagnies } = useAppSelector((state) => state.beneficiary);
    const dispatch = useAppDispatch();

    /**
     * Get a specific company setting (returns boolean for those with '0' or '1' as content)
     * @param name Name of the setting to get
     * @param companyId Id of the company inside which you're searching the setting
     * @returns The setting content or null if not found
     */
    const getBPCompanySettings = (name: string, companyId: number) => {
        if (!beneficiarCompagnies) return null;
        const company = beneficiarCompagnies.find((company) => company.id === companyId);
        if (!company) return null;
        const setting = company.settings.find((setting) => setting.name === name);
        if (!setting) return null;
        const content = setting.content;
        if (content == "0" || content == "1") return !!+content;
        return content;
    };

    const getBPCompanyInfos = (companyId: number) => {
        if (!beneficiarCompagnies) return null;
        const company = beneficiarCompagnies.find((company) => company.id === companyId);
        if (!company) return null;
        return company;
    };

    useEffect(() => {
        if (!beneficiarCompagnies && portalType === "beneficiary") dispatch(getCompagnies());
    }, [beneficiarCompagnies]);

    return { beneficiarCompagnies, getBPCompanySettings, getBPCompanyInfos };
};
